import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { MembershipProvider } from './contexts/MembershipContext';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import StudyPlanner from './pages/StudyPlanner';
import StudySession from './pages/StudySession';
import QuickReview from './pages/QuickReview';
import WeakAreas from './pages/WeakAreas';
import Practice from './pages/Practice';
import Analytics from './pages/Analytics';
import MissedQuestions from './pages/MissedQuestions';
import Review from './pages/Review';
import CBSE from './pages/CBSE';
import CBSEReview from './pages/CBSEReview';
import Admin from './pages/Admin';
import Payment from './pages/Payment';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import USMLESteps from './pages/USMLESteps';
import Library from './pages/Library';
import LibraryContent from './pages/LibraryContent';
import LecturePractice from './pages/LecturePractice';
import LearnMode from './pages/LearnMode';
import ShelfExam from './pages/ShelfExam';
import ProfessorDashboard from './pages/ProfessorDashboard';
import TakeQuiz from './pages/TakeQuiz';
import ScratchPadView from './pages/ScratchPadView';

// Protected Route Component
function ProtectedRoute({ children }) {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }
    
    return children;
}

// Public Route (redirect if authenticated)
function PublicRoute({ children }) {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
            </div>
        );
    }
    
    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }
    
    return children;
}

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={
                <PublicRoute>
                    <Login />
                </PublicRoute>
            } />
            <Route path="/register" element={
                <PublicRoute>
                    <Register />
                </PublicRoute>
            } />
            <Route path="/forgot-password" element={
                <PublicRoute>
                    <ForgotPassword />
                </PublicRoute>
            } />
            <Route path="/reset-password/:token" element={
                <PublicRoute>
                    <ResetPassword />
                </PublicRoute>
            } />
            
            {/* Protected Routes */}
            <Route path="/dashboard" element={
                <ProtectedRoute>
                    <Dashboard />
                </ProtectedRoute>
            } />
            <Route path="/study-planner" element={
                <ProtectedRoute>
                    <StudyPlanner />
                </ProtectedRoute>
            } />
            <Route path="/study-session/:sessionId" element={
                <ProtectedRoute>
                    <StudySession />
                </ProtectedRoute>
            } />
            <Route path="/quick-review" element={
                <ProtectedRoute>
                    <QuickReview />
                </ProtectedRoute>
            } />
            <Route path="/weak-areas" element={
                <ProtectedRoute>
                    <WeakAreas />
                </ProtectedRoute>
            } />
            <Route path="/practice" element={
                <ProtectedRoute>
                    <Practice />
                </ProtectedRoute>
            } />
            <Route path="/analytics" element={
                <ProtectedRoute>
                    <Analytics />
                </ProtectedRoute>
            } />
            <Route path="/missed" element={
                <ProtectedRoute>
                    <MissedQuestions />
                </ProtectedRoute>
            } />
            <Route path="/review" element={
                <ProtectedRoute>
                    <Review />
                </ProtectedRoute>
            } />
            <Route path="/cbse" element={
                <ProtectedRoute>
                    <CBSE />
                </ProtectedRoute>
            } />
            <Route path="/cbse/review/:examId" element={
                <ProtectedRoute>
                    <CBSEReview />
                </ProtectedRoute>
            } />
            <Route path="/admin" element={
                <ProtectedRoute>
                    <Admin />
                </ProtectedRoute>
            } />
            <Route path="/payment" element={
                <ProtectedRoute>
                    <Payment />
                </ProtectedRoute>
            } />
            <Route path="/payment/success" element={
                <ProtectedRoute>
                    <PaymentSuccess />
                </ProtectedRoute>
            } />
            <Route path="/payment/cancel" element={
                <ProtectedRoute>
                    <PaymentCancel />
                </ProtectedRoute>
            } />
            <Route path="/subscription/success" element={
                <ProtectedRoute>
                    <PaymentSuccess />
                </ProtectedRoute>
            } />
            <Route path="/subscription/cancel" element={
                <ProtectedRoute>
                    <PaymentCancel />
                </ProtectedRoute>
            } />
            <Route path="/usmle-steps" element={
                <ProtectedRoute>
                    <USMLESteps />
                </ProtectedRoute>
            } />
            <Route path="/shelf-exam" element={
                <ProtectedRoute>
                    <ShelfExam />
                </ProtectedRoute>
            } />
            <Route path="/library" element={
                <ProtectedRoute>
                    <Library />
                </ProtectedRoute>
            } />
            <Route path="/library/:category/:systemOrSubject/:topic" element={
                <ProtectedRoute>
                    <LibraryContent />
                </ProtectedRoute>
            } />
            <Route path="/lecture-practice/:sessionId" element={
                <ProtectedRoute>
                    <LecturePractice />
                </ProtectedRoute>
            } />
            <Route path="/lecture-learn/:uploadId" element={
                <ProtectedRoute>
                    <LearnMode />
                </ProtectedRoute>
            } />
            <Route path="/professor" element={
                <ProtectedRoute>
                    <ProfessorDashboard />
                </ProtectedRoute>
            } />
            <Route path="/scratchpad/:padId" element={
                <ProtectedRoute>
                    <ScratchPadView />
                </ProtectedRoute>
            } />
            <Route path="/quiz" element={<TakeQuiz />} />
            
            {/* Catch all - redirect to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <ThemeProvider>
                <AuthProvider>
                    <MembershipProvider>
                        <AppRoutes />
                        <Toaster position="top-right" richColors />
                    </MembershipProvider>
                </AuthProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}

export default App;
